import React, { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext, apiClient } from '../App';
import BulkInvitationResults from './BulkInvitationResults';

export default function CreationInvitationProgress({ gameId }) {
  const { user, beginUserUpdate } = useContext(AuthContext);
  const [view, setView] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const context = useRef(null);
  const key = `${user?.id || ''}:${gameId || ''}`;
  if (context.current?.key !== key || !context.current.generation.isCurrent()) {
    if (context.current) context.current.active = false;
    context.current = { key, active: false, sequence: 0, generation: beginUserUpdate() };
  }
  const current = context.current;
  const validView = view?.context === current ? view.data : null;
  async function load(more = false) {
    const generation = beginUserUpdate();
    const sequence = ++current.sequence;
    const valid = () => context.current === current && current.active && sequence === current.sequence && generation.isCurrent();
    setLoading(true);
    try {
      let data;
      if (more && validView?.next_cursor != null) {
        data = (await apiClient.get(`/creation-invite-operations/${validView.operation_id}`, { params: { after: validView.next_cursor } })).data;
        data = { ...data, results: [...validView.results, ...data.results] };
      } else data = (await apiClient.get(`/games/${gameId}/creation-invite-operation`)).data.operation;
      if (valid()) { setView({ context: current, data }); setError(''); }
    } catch (e) {
      if (valid()) {
        const denied = [401, 403, 404].includes(e?.response?.status);
        if (denied) setView(null);
        setError(denied ? 'Current host or cohost access is required.' : 'Invitation progress could not be refreshed. Completed results are preserved.');
      }
    } finally { if (valid()) setLoading(false); }
  }
  useEffect(() => {
    current.active = true; setError(''); setLoading(false);
    if (gameId && user?.id) load();
    return () => { current.active = false; current.sequence += 1; };
    // Account/session generation owns all callbacks, including A -> B -> A.
  }, [current]);
  useEffect(() => {
    if (!validView || !['processing', 'needs_attention'].includes(validView.state)) return undefined;
    const timer = setTimeout(() => load(), 5000);
    return () => clearTimeout(timer);
  }, [validView, current]);
  if (!validView && !error) return null;
  return <section aria-label="Create and Invite progress" className="my-4 rounded-xl border border-orange-200 p-3">
    <h3 className="font-semibold">Create &amp; Invite</h3>
    {validView && <>
      <p role="status">{validView.state === 'needs_attention' ? 'Needs attention: some work could not be resolved automatically.' : validView.state === 'processing' ? 'Invitations are processing. You can leave this page.' : validView.state === 'completed_with_failures' ? 'Completed with failures' : 'Invitation processing complete'}</p>
      <p className="text-sm">{validView.result_summary?.evaluated_count || 0} members evaluated · {validView.result_summary?.pending_count || 0} pending processing</p>
      <BulkInvitationResults data={{ ...validView, results: validView.details_expired ? null : validView.results }} />
      {validView.details_expired ? <p>Detailed results are no longer retained</p> : <p className="text-sm">Showing {validView.results?.length || 0} of {validView.result_summary?.evaluated_count || 0} member results</p>}
      {validView.next_cursor != null && <button type="button" className="min-h-[44px] underline" disabled={loading} onClick={() => load(true)}>Load more results</button>}
    </>}
    {error && <p role="alert">{error}</p>}
    <button type="button" className="min-h-[44px] underline" disabled={loading} onClick={() => load()}>{loading ? 'Refreshing…' : 'Refresh progress'}</button>
    <p className="text-xs">Invitations do not reserve spots or prove that a notification was received.</p>
  </section>;
}
